import React from 'react';
import Select, { components, OptionProps } from 'react-select';
import { GuideHintTooltip } from './guide/GuideHintTooltip';
import type { GuideTarget } from './guide/guideContent';
import { ReactComponent as ChevronDownIcon } from './assets/chevron.svg';
import { ReactComponent as CheckIcon } from './assets/check.svg';
import styles from './SocialGraphPOC.module.scss';

export interface PanelSelectOption {
  value: string;
  label: string;
  // shows as a hint bubble to the right of the open menu while the option is
  // hovered (Figma 683-1325); also read to screen readers with the option
  hint?: string;
  // guide chapter the hint bubble's "Learn more" opens
  guide?: GuideTarget;
}

// the per-select tooltip instance and the option anchors agree on this id
const optionHintTooltipId = (inputId: string) => `${inputId}OptionHint`;

// selected option shows a check mark at the far edge (Figma node 272-27).
// The flex row lives on our own inner wrapper: react-select keeps a functional
// `display: block` on the option element even in unstyled mode (emotion styles
// land after the CSS modules, so they'd override a flex display set there)
const Option: React.FC<OptionProps<PanelSelectOption, false>> = (props) => {
  const { hint, guide } = props.data;
  const innerProps = hint
    ? {
        ...props.innerProps,
        'data-tooltip-id': optionHintTooltipId(props.selectProps.inputId ?? ''),
        'data-tooltip-content': hint,
        'data-guide-page': guide?.page,
        'data-guide-section': guide?.section,
      }
    : props.innerProps;
  return (
    <components.Option {...props} innerProps={innerProps}>
      <span className={styles.panelSelectOptionInner}>
        <span className={styles.panelSelectOptionLabel}>{props.children}</span>
        {hint && <span className={styles.srOnly}>{hint}</span>}
        {props.isSelected && <CheckIcon className={styles.panelSelectCheck} aria-hidden="true" />}
      </span>
    </components.Option>
  );
};

const DropdownIndicator: React.FC = () => (
  <ChevronDownIcon className={styles.panelSelectChevron} aria-hidden="true" />
);

// Dark-canvas select for the graph side panels (Figma nodes 272-18 control,
// 272-28 menu, 272-27 options). react-select runs `unstyled` so every visual
// lives in the SCSS module alongside the rest of the panel styles.
export const PanelSelect: React.FC<{
  inputId: string;
  value: string;
  options: PanelSelectOption[];
  onChange: (value: string) => void;
  isDisabled?: boolean;
  'aria-labelledby'?: string;
}> = ({ inputId, value, options, onChange, isDisabled, 'aria-labelledby': ariaLabelledBy }) => (
  <>
    <Select<PanelSelectOption>
      inputId={inputId}
      aria-labelledby={ariaLabelledBy}
      value={options.find((option) => option.value === value) ?? null}
      options={options}
      isDisabled={isDisabled}
      isSearchable={false}
      unstyled
      onChange={(option) => {
        if (option) onChange(option.value);
      }}
      components={{ Option, DropdownIndicator, IndicatorSeparator: null }}
      className={styles.panelSelect}
      classNames={{
        control: ({ menuIsOpen, isDisabled: disabled }) =>
          [
            styles.panelSelectControl,
            menuIsOpen ? styles.panelSelectControlOpen : '',
            disabled ? styles.panelSelectControlDisabled : '',
          ].join(' '),
        singleValue: () => styles.panelSelectValue,
        menu: () => styles.panelSelectMenu,
        option: ({ isFocused }) =>
          [styles.panelSelectOption, isFocused ? styles.panelSelectOptionFocused : ''].join(' '),
      }}
    />
    {options.some((option) => option.hint) && (
      <GuideHintTooltip id={optionHintTooltipId(inputId)} place="right" />
    )}
  </>
);
